import * as React from 'react';
import { PortableText, PortableTextReactComponents } from '@portabletext/react';
import { format } from 'date-fns';
import NextImage from 'next/image';
import { Article, Category, TOCItem } from 'constants/blog';
import { urlForImage } from 'services/server/sanity/image';
import { toSlug } from 'utils/shared/string/generateSlug';
import BounceLogoSplash from 'svg/BounceLogoSplash';
import { ButtonLink } from 'components/Button';
import Footer from 'components/Footer';
import NewsLetterSubscriptionForm from 'components/NewsLetterSubscriptionForm';
import AuthorByline from 'components/blog/AuthorByline';
import AuthorCard from 'components/blog/AuthorCard';
import Conclusion from 'components/blog/Conclusion';
import DraftIndicator from 'components/blog/DraftIndicator';
import LatestPosts from 'components/blog/LatestPosts';
import ShareArticle from 'components/blog/ShareArticle';
import TableOfContents from 'components/blog/TableOfContents';
import Tag from 'components/blog/Tag';
import TopNav from 'components/nav/TopNav/TopNav';
import Head from 'components/utilities/Head';
import PoppinsFont from 'components/utilities/PoppinsFont';
import { getArticlePageUrl } from 'constants/pages';

const LATEST_POSTS_ANCHOR = 'latestPosts';

interface Props {
  articles: Article[];
  article: Article;
  jsonLd?: string;
  readingTime: number;
  tableOfContents: TOCItem[];
}

const ImageComponentPortableText = ({ value }: any) => {
  if (!value) return null;
  const imageUrl = value.asset ? urlForImage(value) : '';
  return (
    <img
      src={imageUrl}
      alt={value?.alt || 'Image'}
      title={value?.alt || 'Image'}
      loading="lazy"
      className="object-cover"
      sizes="(max-width: 800px) 100vw, 800px"
    />
  );
};

export default function ArticlePage(props: Props) {
  const { articles, article, jsonLd, readingTime, tableOfContents } = props;

  if (!article) {
    return null;
  }

  const articleUrl = `${process.env.NEXT_PUBLIC_APP_URL || 'https://www.bounce.game'}${getArticlePageUrl(article.slug)}`;

  const components: Partial<PortableTextReactComponents> = {
    types: {
      image: ImageComponentPortableText,
      table: ({ value }) => {
        const { rows } = value;
        if (!rows) return null;

        return (
          <table>
            <tbody>
              {rows.map((row: any, index: number) => (
                <tr key={row._key} className={index === 0 ? 'font-bold' : ''}>
                  {row.cells.map((cell: any, cellIndex: number) => (
                    <td key={cellIndex}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      },
    },
    block: {
      h1: ({ children }) => <h1 className="text-2xl">{children}</h1>,
      h2: ({ children }) => {
        const text = children?.toString() || '';
        const id = toSlug(text);
        return (
          <h2 id={id} className="scroll-mt-20">
            {children}
          </h2>
        );
      },
      h3: ({ children }) => {
        const text = children?.toString() || '';
        const id = toSlug(text);
        return (
          <h3 id={id} className="scroll-mt-20">
            {children}
          </h3>
        );
      },
      blockquote: ({ children }) => (
        <blockquote className="border-l-color-text-brand">{children}</blockquote>
      ),
    },
  };

  return (
    <>
      <Head
        title={article.title}
        description={article.previewDescription}
        ogImage={article?.mainImage}
        canonicalType="currentUrlStripQuery"
      />
      <PoppinsFont />
      <TopNav
        shouldShowMobile
        shouldLinkToAuthPage={false}
        shouldShowAdditionalLinks
        shouldHideNavigation={false}
        isBlur
      />

      <main className="min-h-screen">
        <div className="flex flex-col bg-color-bg-lightmode-primary px-4 dark:bg-color-bg-darkmode-primary md:px-0">
          <div className="mx-auto max-w-[60rem] pb-12">
            <DraftIndicator isDraft={article._id?.startsWith('drafts.') || !article.publishedAt} />
            <header className="mt-12 flex flex-col mb-8">
              {/* Publication Meta */}
              <div className="mb-3">
                <span className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {article.publishedAt && (
                    <>Published {format(new Date(article.publishedAt), 'd LLL yyyy')}</>
                  )}
                  {article.publishedAt && readingTime && ' · '}
                  {readingTime && `${readingTime} min read`}
                </span>
              </div>

              {/* Title - Smaller size */}
              <h1 className="typography-product-heading text-pretty md:text-3xl lg:text-4xl">
                {article.title}
              </h1>

              {/* Subtitle — serves as the article TL;DR for AI extraction (see speakable schema). */}
              <p className="article-tldr typography-informative-subheading-compact mt-2  font-light text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary md:text-xl">
                {article.previewDescription}
              </p>

              {/* Category Tags */}
              {article.categories && article.categories.length > 0 && <div className="my-4 flex justify-start">
                {article.categories?.filter(Boolean).map((category: Category, index: number) => {
                  return (
                    <Tag
                      text={category.title}
                      key={index}
                      appendClasses={false}
                      className={
                        'mr-2 inline-block rounded-full bg-color-bg-lightmode-tertiary px-3 py-2 text-xs font-medium text-color-text-lightmode-secondary md:text-sm'
                      }
                    />
                  );
                })}
              </div>}

              {/* Author & Share Row */}
              <div className="my-4 flex flex-col items-center justify-center md:justify-between w-full gap-4 md:flex-row md:gap-8">
                {/* Author Info */}
                <AuthorByline
                  author={article.author}
                  showDate={false}
                />

                {/* Separator - Desktop only */}
                <div className="hidden h-8 w-px bg-color-border-input-lightmode dark:bg-color-border-input-darkmode md:block" />

                {/* Share Section */}
                <div className="flex items-center gap-4">
                  <span className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    Share
                  </span>
                  <ShareArticle
                    articleUrl={articleUrl}
                    articleTitle={article.title}
                    articleDescription={article.previewDescription}
                  />
                </div>
              </div>
            </header>
            <NextImage
              src={article?.mainImage}
              alt={article.title}
              className="mx-auto w-full max-w-[50rem] rounded-xl object-cover object-center"
              loading="lazy"
              height={480}
              width={800}
            />
          </div>
          <div className="mx-auto max-w-[1400px] px-4 md:px-8">
            <div className="flex justify-center gap-8">
              {/* TOC Sidebar - Desktop */}
              {tableOfContents.length > 0 && (
                <aside className="hidden w-60 shrink-0 lg:block">
                  <TableOfContents items={tableOfContents} />
                </aside>
              )}

              <div className="w-full max-w-[45rem]">
                {/* TOC Accordion - Mobile */}
                {tableOfContents.length > 0 && (
                  <div className="mb-8 lg:hidden">
                    <TableOfContents items={tableOfContents} collapsible />
                  </div>
                )}

                {/* Article Body */}
                <article className="prose mb-16 dark:prose-invert lg:prose-xl">
                  <PortableText value={article.body} components={components} />
                </article>

                {article?.conclusion && <Conclusion body={article.conclusion} />}
              </div>
            </div>
            <div className="mt-16 border-t border-color-border-input-lightmode pt-8 dark:border-color-border-input-darkmode">
              <AuthorCard
                author={article.author}
                articleUrl={articleUrl}
              />
            </div>

            <div className="mt-10 flex flex-col items-center rounded-2xl bg-color-bg-lightmode-invert px-6 py-10 text-center md:items-start md:text-left">
              <h3 className="text-2xl font-bold text-white md:text-3xl">
                Ready to hit the court?
              </h3>
              <p className="mt-2 text-base text-white/80">
                Book courts and lessons that fit your week.
              </p>
              <ButtonLink
                href="/"
                variant="secondary"
                className="mt-6"
                isInline
              >
                Get started
              </ButtonLink>
            </div>
          </div>
        </div>
        <LatestPosts sectionAnchor={LATEST_POSTS_ANCHOR} data={articles} />
        <NewsLetterSubscriptionForm />
        <Footer />
      </main>
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
    </>
  );
}
